import { useState, useMemo } from 'react';
import { Car, LogEntry } from '../lib/supabase';
import { History, ChevronRight } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { formatCurrency } from '../lib/conversions';
import { useParams, useOutletContext } from 'react-router-dom';
import { theme, styles } from '../lib/theme';
import { useTranslations } from '../i18n';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { SupportedLanguage } from '../lib/expenseTypeHelpers';

type ExpensesListProps = {
  car: Car;
  logEntries: LogEntry[];
  loading?: boolean;
  isPublicView?: boolean;
  highlightId?: string | null;
};

export function ExpensesList({ car, logEntries, loading, isPublicView, highlightId }: ExpensesListProps) {
  const { preferences } = usePreferences();
  const { t, language } = useTranslations();
  const { slug } = useParams();
  const outletContext = useOutletContext<any>() || {};
  const isAdminView = outletContext.isAdminView || false;
  const ownerPreferences = outletContext.ownerPreferences;

  const effectivePreferences = (ownerPreferences || preferences) ? {
    ...(ownerPreferences || preferences),
    distance_unit: car.distance_unit || (ownerPreferences || preferences).distance_unit,
    volume_unit: car.volume_unit || (ownerPreferences || preferences).volume_unit,
    currency: car.currency || (ownerPreferences || preferences).currency,
    currency_symbol: car.currency_symbol || (ownerPreferences || preferences).currency_symbol
  } : null;
  const currentLanguage = language as SupportedLanguage;

  const [statsPeriod, setStatsPeriod] = useState<'year' | 'all'>('year');




  // Grouping logic
  const groupedEntries = useMemo(() => {
    const groups: Record<string, Record<string, LogEntry[]>> = {};
    logEntries.forEach((entry) => {
      const date = new Date(entry.date);
      if (isNaN(date.getTime())) return;

      const year = date.getFullYear().toString();
      const month = date.toLocaleDateString(currentLanguage, { month: 'long' });

      if (!groups[year]) groups[year] = {};
      if (!groups[year][month]) groups[year][month] = [];
      groups[year][month].push(entry);
    });
    return groups;
  }, [logEntries]);

  const sortedYears = useMemo(() => {
    return Object.keys(groupedEntries).sort((a, b) => b.localeCompare(a));
  }, [groupedEntries]);

  // Stats calculation
  const stats = useMemo(() => {
    if (logEntries.length === 0) return null;

    const entriesWithMileage = logEntries.filter(e => e.mileage && e.mileage > 0);

    // Helper to calculate total value of an entry
    const getEntryTotal = (entry: LogEntry) => {
      return (entry.expenses || [])
        .filter(exp => exp.type !== 'info')
        .reduce((sum, exp) => sum + (exp.amount || 0), 0);
    };

    // All time
    const allTotal = logEntries.reduce((sum, e) => sum + getEntryTotal(e), 0);
    const firstDate = new Date(logEntries[logEntries.length - 1].date);
    const lastDate = new Date(logEntries[0].date);
    const monthsDiff = Math.max(1, (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + (lastDate.getMonth() - firstDate.getMonth()) + 1);
    const allAvgMonthly = allTotal / monthsDiff;

    let allDistance = car.mileage;
    if (entriesWithMileage.length >= 2) {
      const mileages = entriesWithMileage.map(e => e.mileage as number);
      allDistance = Math.max(...mileages) - Math.min(...mileages);
    }
    const allCostPerKm = allDistance > 0 ? allTotal / allDistance : 0;

    // Latest year
    const year = sortedYears[0];
    const yearEntries = logEntries.filter(e => new Date(e.date).getFullYear().toString() === year);
    const yearTotal = yearEntries.reduce((sum, e) => sum + getEntryTotal(e), 0);
    const yearAvgMonthly = yearTotal / 12;

    const yearEntriesWithMileage = yearEntries.filter(e => e.mileage && e.mileage > 0);
    let yearDistance = 0;
    if (yearEntriesWithMileage.length >= 2) {
      const mileages = yearEntriesWithMileage.map(e => e.mileage as number);
      yearDistance = Math.max(...mileages) - Math.min(...mileages);
    } else {
      yearDistance = 0;
    }
    const yearCostPerKm = yearDistance > 0 ? yearTotal / yearDistance : 0;

    return {
      year: {
        label: `${t.expenses.stats.annual} ${year}`,
        total: yearTotal,
        avg: yearAvgMonthly,
        cpk: yearCostPerKm,
        distance: yearDistance
      },
      all: {
        label: t.expenses.stats.allTime,
        total: allTotal,
        avg: allAvgMonthly,
        cpk: allCostPerKm,
        distance: allDistance
      }
    };
  }, [sortedYears, logEntries, car.mileage]);

  const currentStats = stats ? stats[statsPeriod] : null;


  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 font-primary">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500 font-semibold italic">{t.common.loading}</p>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
        {/* Stats Summary Card */}

        {/* Expenses History List */}
        <div className="space-y-10">
          {logEntries.length === 0 ? (
            <div className={`${styles.card} p-12 text-center shadow-sm`}>
              <History size={48} className="text-gray-200 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">{t.expenses.stats.noExpenses}</p>
            </div>
          ) : (
            <>
              {sortedYears.map(year => (
                <div key={year} className="space-y-6 pt-6 first:pt-0">
                  <button 
                    onClick={() => {
                      sessionStorage.setItem('stats_selectedYear', year);
                      if (outletContext.setActiveTab) {
                        outletContext.setActiveTab('stats');
                      }
                    }}
                    className="flex items-center gap-2 group transition-all active:scale-[0.98] px-2"
                  >
                    <h2 className={theme.typography.sectionTitle}>
                      {year}
                    </h2>
                    <ChevronRight size={20} className="text-blue-600 dark:text-blue-400 mt-1" strokeWidth={2.5} />
                  </button>
                  {Object.entries(groupedEntries[year])
                    .map(([month, monthEntries]) => {
                      const monthTotal = monthEntries.reduce((sum, entry) => {
                        return sum + (entry.expenses || [])
                          .filter(e => e.type !== 'info')
                          .reduce((expSum, e) => expSum + (e.amount || 0), 0);
                      }, 0);

                      return (
                        <div key={month} className="space-y-2">
                          <div className="flex items-center justify-between px-1">
                            <h3 className={theme.typography.sectionLabel}>{month} {year}</h3>
                            <span className="text-[13px] font-medium text-gray-400 dark:text-gray-300">
                              {effectivePreferences ? formatCurrency(monthTotal, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${monthTotal.toFixed(2)}`}
                            </span>
                          </div>

                          <div className={styles.card}>
                            <div className="divide-y divide-gray-100 dark:divide-white/5">
                              {monthEntries.map((entry) => (
                                <LogEntryItem
                                  key={entry.id}
                                  entry={entry}
                                  slug={slug || ''}
                                  isAdminView={isAdminView}
                                  isPublicView={isPublicView}
                                  shareLocationsEnabled={car.share_locations}
                                  effectivePreferences={effectivePreferences}
                                  highlighted={highlightId === entry.id}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ))}
            </>
          )}
        </div>

        {/* FAB */}

      </div>
    </div>
  );
}
